/**
 * Grouping, group roll-ups, and row sorting for the Availability report.
 *
 * Groups are sorted by label, with the Unassigned group (no Home Role / no
 * Discipline / no Account / no Manager) always LAST so a data gap never sits
 * on top of the real groups. Rows keep their input order inside a group — sort
 * them with {@link sortRows}.
 *
 * A group cell is Σbooked ÷ Σavailable over the members who have a PLAN for the
 * month (Guidance, or the month is inside the plan horizon) and positive
 * Available Hours. When fewer than half the members have a plan the group cell
 * is Unplanned: a roll-up of a minority would read as the whole group.
 *
 * 🔒 FIREWALL: HOURS only. No Fit Score, Capacity, Intensity, or points imports.
 */
import {
  type AvailabilityCutoffs,
  type CellState,
  classifyCell,
  isOverState,
} from "@/lib/availability/cell-state";

export type GroupBy = "homeRole" | "discipline" | "account" | "manager" | "none";

export type RowSort = "mostAvailable" | "mostOver" | "name";

export interface AvailabilityCellInput {
  availableHours: number | null;
  bookedHours: number;
  hasGuidance: boolean;
  pastPlanHorizon: boolean;
}

export interface AvailabilityRow {
  resourceId: string;
  name: string;
  homeRole: string | null;
  disciplineCode: string | null;
  disciplineName: string | null;
  primaryAccount: { id: string; name: string } | null;
  otherAccountCount: number;
  managerName: string | null;
  /** Keyed by `"YYYY-MM-01"`. A missing month is treated as no data. */
  cells: Record<string, AvailabilityCellInput>;
}

export interface AvailabilityGroup<R extends AvailabilityRow = AvailabilityRow> {
  /** The grouping value, or null for the Unassigned group. */
  key: string | null;
  label: string;
  isUnassigned: boolean;
  rows: R[];
}

export interface GroupCellResult {
  state: CellState;
  /** Σbooked ÷ Σavailable × 100 over planned members. Null when No Data or Unplanned. */
  pct: number | null;
  /** Σavailable − Σbooked over planned members, one decimal. */
  netHours: number;
  /** Members whose own cell is Over-utilized OR Stretched — anyone showing a "+N". */
  overCount: number;
}

const UNASSIGNED_LABEL: Record<Exclude<GroupBy, "none">, string> = {
  homeRole: "No Home Role",
  discipline: "No Discipline",
  account: "No Account",
  manager: "No Manager",
};

/** Label for the single group when grouping is off. */
export const ALL_RESOURCES_LABEL = "All Resources";

/** A blank string is as unassigned as a null. */
function clean(value: string | null): string | null {
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function groupingOf(
  row: AvailabilityRow,
  groupBy: Exclude<GroupBy, "none">,
): { key: string; label: string } | null {
  switch (groupBy) {
    case "homeRole": {
      const role = clean(row.homeRole);
      return role === null ? null : { key: role, label: role };
    }
    case "discipline": {
      const code = clean(row.disciplineCode);
      return code === null ? null : { key: code, label: clean(row.disciplineName) ?? code };
    }
    case "account":
      return row.primaryAccount === null
        ? null
        : { key: row.primaryAccount.id, label: row.primaryAccount.name };
    case "manager": {
      const manager = clean(row.managerName);
      return manager === null ? null : { key: manager, label: manager };
    }
  }
}

/** Ordered groups. Empty input yields no groups, whatever `groupBy` is. */
export function groupRows<R extends AvailabilityRow>(
  rows: readonly R[],
  groupBy: GroupBy,
): AvailabilityGroup<R>[] {
  if (rows.length === 0) return [];
  if (groupBy === "none") {
    return [{ key: "all", label: ALL_RESOURCES_LABEL, isUnassigned: false, rows: [...rows] }];
  }

  const byKey = new Map<string, AvailabilityGroup<R>>();
  const unassigned: R[] = [];
  for (const row of rows) {
    const grouping = groupingOf(row, groupBy);
    if (grouping === null) {
      unassigned.push(row);
      continue;
    }
    const existing = byKey.get(grouping.key);
    if (existing === undefined) {
      byKey.set(grouping.key, {
        key: grouping.key,
        label: grouping.label,
        isUnassigned: false,
        rows: [row],
      });
    } else {
      existing.rows.push(row);
    }
  }

  const groups = [...byKey.values()].sort(
    (a, b) => a.label.localeCompare(b.label) || (a.key ?? "").localeCompare(b.key ?? ""),
  );
  if (unassigned.length > 0) {
    groups.push({
      key: null,
      label: UNASSIGNED_LABEL[groupBy],
      isUnassigned: true,
      rows: unassigned,
    });
  }
  return groups;
}

/** A member has a plan for a month when it has Guidance or sits inside the horizon. */
export function hasPlan(cell: AvailabilityCellInput | undefined): cell is AvailabilityCellInput {
  if (cell === undefined) return false;
  if (cell.availableHours === null || cell.availableHours <= 0) return false;
  return cell.hasGuidance || !cell.pastPlanHorizon;
}

/** Round to one decimal place, so a stored value and a displayed one agree. */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

export function groupCell(
  rows: readonly AvailabilityRow[],
  month: string,
  cutoffs: AvailabilityCutoffs,
): GroupCellResult {
  let available = 0;
  let booked = 0;
  let planned = 0;
  let overCount = 0;
  for (const row of rows) {
    const cell = row.cells[month];
    if (cell === undefined) continue;
    if (isOverState(classifyCell(cell, cutoffs).state)) overCount++;
    if (hasPlan(cell)) {
      planned++;
      available += cell.availableHours ?? 0;
      booked += cell.bookedHours;
    }
  }

  const netHours = round1(available - booked);
  const anyData = rows.some((row) => {
    const hours = row.cells[month]?.availableHours;
    return hours !== undefined && hours !== null && hours > 0;
  });
  if (!anyData) return { state: "noData", pct: null, netHours: 0, overCount };
  if (planned * 2 < rows.length) return { state: "unplanned", pct: null, netHours, overCount };

  const rolled = classifyCell(
    { availableHours: available, bookedHours: booked, pastPlanHorizon: false, hasGuidance: true },
    cutoffs,
  );
  return { state: rolled.state, pct: rolled.pct, netHours: rolled.netHours, overCount };
}

/** Σ(available − booked) over the window months the row has a plan for. */
export function windowNetHours(row: AvailabilityRow, windowMonths: readonly string[]): number {
  let net = 0;
  for (const month of windowMonths) {
    const cell = row.cells[month];
    if (hasPlan(cell)) net += (cell.availableHours ?? 0) - cell.bookedHours;
  }
  return round1(net);
}

/**
 * A sorted copy. Most Available = largest summed net first; Most Over = most
 * negative first; Name = alphabetical. Ties fall back to name, then id.
 */
export function sortRows<R extends AvailabilityRow>(
  rows: readonly R[],
  sort: RowSort,
  windowMonths: readonly string[],
): R[] {
  const byName = (a: R, b: R) =>
    a.name.localeCompare(b.name) || a.resourceId.localeCompare(b.resourceId);
  if (sort === "name") return [...rows].sort(byName);

  const net = new Map(rows.map((row) => [row.resourceId, windowNetHours(row, windowMonths)]));
  const direction = sort === "mostAvailable" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const diff = (net.get(a.resourceId) ?? 0) - (net.get(b.resourceId) ?? 0);
    return diff !== 0 ? diff * direction : byName(a, b);
  });
}
